/**
 * Frontend Branding Types
 *
 * These types mirror the backend types but are optimized for frontend use
 */

export enum TenantType {
  /** @deprecated Use TEAM instead */
  BRAND_SPACE = 'BRAND_SPACE',
  TEAM = 'TEAM',
  /** @deprecated Use AGENCY instead */
  ORGANIZATION = 'ORGANIZATION',
  AGENCY = 'AGENCY',
}

export enum BrandingAssetType {
  // Logos
  LOGO_BIG = 'logo_big',
  LOGO_SMALL = 'logo_small',
  LOGO_FAVICON = 'logo_favicon',

  // Header images
  DASHBOARD_HEADER = 'dashboard_header',
  CALENDAR_HEADER = 'calendar_header',
  LIST_HEADER = 'list_header',
  BRANDCHECK_HEADER = 'brandcheck_header',
  RELEASE_HEADER = 'release_header',
  AGENCYCC_HEADER = 'agencycc_header',
  APPROVAL_HEADER = 'approval_header',

  // Footer
  DASHBOARD_FOOTER = 'dashboard_footer',

  // Loading/Misc
  LOADER_GIF = 'loader_gif',
  PLACEHOLDER = 'placeholder',
  OG_IMAGE = 'og_image',
  EMAIL_LOGO = 'email_logo',

  // PWA
  PWA_ICON_192 = 'pwa_icon_192',
  PWA_ICON_512 = 'pwa_icon_512',
  PWA_SPLASH = 'pwa_splash',
}

export interface BrandingColors {
  primary?: string;
  secondary?: string;
  secondaryRgba?: string;
  gray?: string;
  gray2?: string;
  gray400?: string;
  gray600?: string;
  grayStone?: string;
  light?: string;
  calendarHighlight?: string;
  blackButton?: string;
  coral?: string;
  softCoral?: string;
  softMint?: string;
  peach?: string;
  lightTealGray?: string;
  sageGray?: string;
  warmGray?: string;
  offWhite?: string;
}

export interface BrandingAssets {
  // Logos
  logoBig?: string;
  logoSmall?: string;
  logoFavicon?: string;

  // Header images
  dashboardHeader?: string;
  calendarHeader?: string;
  listHeader?: string;
  brandcheckHeader?: string;
  releaseHeader?: string;
  agencyccHeader?: string;
  approvalHeader?: string;

  // Footer
  dashboardFooter?: string;

  // Loading/Misc
  loaderGif?: string;
  placeholder?: string;
  ogImage?: string;
  emailLogo?: string;

  // PWA
  pwaIcon192?: string;
  pwaIcon512?: string;
  pwaSplash?: string;
}

export interface BrandingFont {
  family: string;
  url: string;
  weights?: string[];
  italic?: boolean;
}

export interface Branding {
  id: number;
  tenantType: TenantType;
  tenantId: number;
  colors?: BrandingColors;
  assets?: BrandingAssets;
  font?: BrandingFont;
  headlineFont?: BrandingFont;
  /** Where each resolved value comes from; 'team' = the brand's OWN saved override */
  sources?: {
    colors?: Partial<Record<keyof BrandingColors, 'team' | 'agency' | 'default'>>;
    assets?: Partial<Record<keyof BrandingAssets, 'team' | 'agency' | 'default'>>;
    font?: 'team' | 'agency' | 'default';
    headlineFont?: 'team' | 'agency' | 'default';
  };
  isEnabled: boolean;
  /** Resolved by GET /branding/team/:id: Plan or add-on (incl. inherited Brand Portfolio add-on) */
  isWhitelabelEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Update payload. `null` for a colors/assets/font/headlineFont key means "remove this
 * override" (cascade falls back Brand -> Brand Portfolio -> defaults); an absent key is
 * left unchanged; an empty string is rejected by the API.
 */
export interface BrandingInput {
  colors?: { [K in keyof BrandingColors]?: string | null };
  assets?: { [K in keyof BrandingAssets]?: string | null };
  font?: BrandingFont | null;
  headlineFont?: BrandingFont | null;
  isEnabled?: boolean;
}

export interface AssetUploadResult {
  assetType: BrandingAssetType;
  url: string; // CloudFront URL (s3Key/s3Bucket not exposed for security)
  fileSize: number;
  mimeType: string;
}
